import { FlightDetailsInfoList } from "@flights/search-ui";
import { trackWithDefaultStage } from "@flights/et-universal";
import React, { useRef } from "react";
import useFlightDetailsInfoController from "./useFlightDetailsInfo.controller";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { SkeletonListMultiLine } from "../../../Skeleton/SkeletonListMultiLine";
import { UIFetchStatus, UIFlightData } from "@flights/types";
import useIsInViewport from "../../../../../hooks/useIsInViewport";
import flights_web_condensed_extras_fd_ddot from "../../../../../utils/experiments/funnel/flights_web_condensed_extras_fd_ddot";
import flights_apex_web_show_included_baggage_asap from "../../../../../utils/experiments/apex/flights_apex_web_show_included_baggage_asap";
import useRouteName from "../../../../../hooks/useRouteName";
import { useViewport } from "@bookingcom/bui-react";
import { useTrackSplitBoundsBaggageAsap } from "../../../../../utils/experiments/funnel/flights_split_bounds_us";
import { useFlightCardLuggageController } from "../../../FlightCard/components/elements/FlightCardLuggage/useFlightCardLuggage.controller";
import flights_web_fd_baggage_transparency, {
  useTrackBaggageTransparencyFD
} from "../../../../../utils/experiments/funnel/flights_web_fd_baggage_transparency";
import useBaggageInfo from "./Items/BaggageInfo/useBaggageInfo";
import useDetailedBaggageInfo from "./Items/DetailedBaggageInfo/useDetailedBaggageInfo";

type Props = {
  flight: UIFlightData;
  fetchStatus: UIFetchStatus;
};

const FlightDetailsInfoDesktop = ({ flight, fetchStatus }: Props) => {
  const { isLoading, items } = useFlightDetailsInfoController({ flight, fetchStatus });
  const i18n = useI18n();
  /* start - flights_web_condensed_extras_fd_ddot */
  const condensedExtrasTrackingRef = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(condensedExtrasTrackingRef);
  const routeName = useRouteName();
  flights_web_condensed_extras_fd_ddot.useTrackStages(isInViewport && routeName === "flightdetails" && !isLoading);
  /* end - flights_web_condensed_extras_fd_ddot */
  const { isLarge } = useViewport();
  const isResponsiveFixes = !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);
  const baggageInfo = useBaggageInfo(flight);
  /* start - flights_apex_web_show_included_baggage_asap */
  const detailedBaggageInfo = useDetailedBaggageInfo(flight);

  /* start - flights_apex_web_show_included_baggage_asap */
  const showBaggageInfoAsap =
    flights_apex_web_show_included_baggage_asap.isEligible(flight) &&
    !!flights_apex_web_show_included_baggage_asap.trackWithDefaultStage();
  const baggageInfoAsapItem = flights_web_fd_baggage_transparency.trackWithDefaultStage()
    ? detailedBaggageInfo
    : baggageInfo;

  const loaderRef = flights_apex_web_show_included_baggage_asap.useTrackStages({ isMobile: false, flight });
  /* end - flights_apex_web_show_included_baggage_asap */

  useTrackSplitBoundsBaggageAsap(showBaggageInfoAsap);

  // This is only tracking goals for `flights_web_sr_baggage_transparency` exp,
  // remove it when the exp is stopped
  useFlightCardLuggageController(flight, false, true);

  useTrackBaggageTransparencyFD(flight, !!showBaggageInfoAsap);

  const loadingComponent = (
    <div aria-label={i18n.trans(t("a11y_flights_fd_additional_loading"))} ref={loaderRef}>
      <SkeletonListMultiLine amount={1} />
    </div>
  );

  if (showBaggageInfoAsap) {
    return (
      <>
        <FlightDetailsInfoList
          attributes={{ "data-testid": "flightdetails_ancillaries" }}
          variant="two-columns"
          items={isLoading ? (!!baggageInfoAsapItem ? [baggageInfoAsapItem] : []) : items}
          isResponsiveFixes={isResponsiveFixes}
        />
        {isLoading ? loadingComponent : undefined}
        <div ref={condensedExtrasTrackingRef}></div>
      </>
    );
  }

  return (
    <>
      <FlightDetailsInfoList
        attributes={{ "data-testid": "flightdetails_ancillaries" }}
        variant="two-columns"
        isLoading={isLoading}
        loadingComponent={loadingComponent}
        items={items}
        isResponsiveFixes={isResponsiveFixes}
      />
      <div ref={condensedExtrasTrackingRef}></div>
    </>
  );
};

export default FlightDetailsInfoDesktop;
